/**
 * The in-play camera.
 *
 * Port of the wide camera in `Match::UpdateIngameCamera` (`match.cpp:723-792`). That is the
 * only camera the original ever uses during play: `camMethod` is a local hard-coded to 1, so
 * the birds-eye and tele branches below it are unreachable dead code. See
 * `docs/original-engine/shell-and-data.md` §6.4.
 *
 * Three properties of it are load-bearing and easy to lose in a rewrite:
 *
 *  - **It does not point at the ball.** The aim point is 60% the man in possession and only
 *    40% the ball, pushed a metre further along his facing and up to four metres towards the
 *    goal his side is attacking. The camera looks where the play is going.
 *  - **It lags on purpose.** Every aim point of the last 1.5 seconds is kept and averaged
 *    under a weight curve that peaks in the middle of that window and falls off sharply at
 *    the newest sample, with the original's note that a camera operator cannot foresee the
 *    present that fast. Aiming straight at the live position instead looks robotic.
 *
 * WHERE THE CAMERA IS PUT IS OURS, AND DELIBERATELY NOT THE ORIGINAL'S. Measured in a live
 * match on 2026-08-04, the original's own framing put **26% of the pitch on screen and no part
 * of the stands at any height** — not the crowd, not the advertising hoardings, not the
 * floodlights, not the roof. All of it is modelled and none of it was ever seen; the picture
 * was a narrow 21-degree telephoto pointed at grass. That is not what football looks like.
 *
 * So `tick` below is still the original's, because the aiming and the lag are what make it feel
 * like a camera operator rather than a tripod, and `pose` is not: it places the camera the way a
 * broadcast main camera sits, high on the halfway line and well back, wide enough to hold both
 * touchlines and the stand behind them. The three numbers that decide the framing are named and
 * expressed in metres and degrees, so the next person can reason about them.
 *
 * The four user settings are still the original's own Camera page sliders, but their defaults
 * are ours: each now sits in the middle of its range, and the middle is the framing above.
 */

import { Quaternion, Vector3, type PerspectiveCamera } from 'three';
import { PITCH } from '../core/constants';
import { simToRender, simToRenderQuat } from '../core/coords';
import { seededRandom } from '../core/random';
import { Vector3Smoother } from '../core/smoother';
import type { Match } from '../sim/match';

export interface CameraSettings {
  zoom: number;
  height: number;
  fov: number;
  angle: number;
}

export const DEFAULT_CAMERA_SETTINGS: CameraSettings = {
  zoom: 0.5,
  height: 0.5,
  fov: 0.5,
  angle: 0,
};

/**
 * The framing at the middle of every slider, in metres and degrees.
 *
 * Chosen by looking at it, against how a televised match is shot: high enough to read the shape
 * of the play, far enough back that both touchlines and the stand behind them are in the
 * picture, and tight enough that a player is still a person rather than a dot. Each slider
 * scales one of these about its middle, so a player who wants the old tight view can still have
 * it and a player who wants the whole ground can have that.
 */
const FRAMING = {
  /** Metres above the pitch. */
  height: 30,
  /** Metres beyond the touchline, on the camera's side. */
  setback: 42,
  /** Vertical field of view, degrees. */
  fov: 32,
  /**
   * Degrees to tilt up off the aim point. Purely composition.
   *
   * These four are not independent, and the constraint is worth writing down because tuning
   * one of them by eye moves the others. With the ball in the middle of the pitch, the near
   * touchline sits about 35 degrees below the horizon and the top of the far stand about 8,
   * so the picture has to span some 28 degrees to hold both — and it only holds both if it is
   * also aimed at the middle of that span. Widen the view and the players shrink; raise the
   * camera and the near touchline drops out of the bottom of the frame; tilt up and the same
   * thing happens faster. What is here spans 32 degrees aimed almost exactly at the middle,
   * with about four degrees to spare and a player about 3.8% of the frame tall.
   */
  tiltUp: 0,
};

/** `camPosSize`, `match.cpp:30` — 150 ticks is 1.5 seconds of aim-point history. */
const HISTORY_SIZE = 150;

/** `playerBias`, `match.cpp:734`. */
const PLAYER_BIAS = 0.6;

/**
 * How far the camera can see, metres.
 *
 * The original's own cap was 200 (`match.cpp:791`), which was ample for a camera that only ever
 * saw grass. Ours sees the far stand and the outer wall of the ground behind it, so it needs
 * more. Raised deliberately, and it is not free: the depth buffer's precision is spread over
 * this range, and the distance haze is calibrated against it.
 */
const FAR_CAP = 400;

const X_AXIS = new Vector3(1, 0, 0);
const Z_AXIS = new Vector3(0, 0, 1);

/**
 * Everything the renderer needs, in the simulation's Z-up frame.
 *
 * Split out from `apply` so a test can check the numbers without a WebGL context.
 */
export interface CameraPose {
  /** Camera position in simulation metres. */
  position: Vector3;
  /** Rotation about Z, applied first: the original's `cameraNodeOrientation`. */
  yaw: number;
  /** Rotation about X, applied second: the original's `cameraOrientation`. */
  pitch: number;
  /** Vertical field of view in degrees. */
  fovDegrees: number;
  near: number;
  far: number;
}

/**
 * The shake's own seed, used when nobody hands one in.
 *
 * Any fixed number would do; what matters is that it is fixed. A match passes the run's own
 * seed instead, so two runs of the same match shake identically and two different matches do
 * not.
 */
const DEFAULT_SHAKE_SEED = 0x0c17a3b5;

export class MatchCamera {
  settings: CameraSettings = { ...DEFAULT_CAMERA_SETTINGS };

  private readonly random: () => number;

  /**
   * @param random the stream the shake draws from. It must be repeatable: a picture that
   *   cannot be reproduced cannot be measured, and this was the platform's own `Math.random`
   *   until it was noticed that the amount of shading the flat grass received therefore varied
   *   from one page load to the next.
   */
  constructor(random: () => number = seededRandom(DEFAULT_SHAKE_SEED)) {
    this.random = random;
  }

  /** The aim points of the last 1.5 seconds, oldest first. */
  private readonly history: Vector3[] = [];
  private readonly average = new Vector3Smoother();

  private readonly aim = new Vector3();
  private readonly averaged = new Vector3();
  private readonly yawQuat = new Quaternion();
  private readonly pitchQuat = new Quaternion();
  private readonly simQuat = new Quaternion();

  /**
   * Advance one simulation tick. Call once per fixed tick, after the match has stepped.
   *
   * The shudder is the original's camera shake, scaled by how fast the ball is travelling. It
   * draws from the seeded stream this camera was built with, so the same match filmed twice is
   * filmed identically — which is what makes a change to the picture measurable.
   */
  tick(match: Match, timeMs: number): void {
    const settings = this.settings;
    const possessor = match.designatedPossessionPlayer;

    // match.cpp:735-739 — a blend of ball and man, then led in his direction, then led
    // again towards the goal the team in possession is attacking.
    const aim = match.ball.predict(0, this.aim);
    aim.multiplyScalar(1 - PLAYER_BIAS).addScaledVector(possessor.position, PLAYER_BIAS);
    aim.add(possessor.directionVec);
    aim.x +=
      ((match.teams[0].fadingPossessionAmount - 1) * -match.teams[0].side +
        (match.teams[1].fadingPossessionAmount - 1) * -match.teams[1].side) *
      4;

    // match.cpp:741 — a lofted ball lifts the aim point only a tenth as far as it rises.
    aim.z *= 0.1;

    // match.cpp:743-746. Note the original's pitchHalfW is the goal-to-goal half length.
    const height = settings.height * 1.5;
    const zoomDivisor = 1 / (settings.zoom + 0.01);
    const maxAlongPitch = PITCH.halfLength * 0.84 * zoomDivisor;
    const maxAcrossPitch = PITCH.halfWidth * 0.6 * zoomDivisor * (height * 0.75 + 0.25);
    if (Math.abs(aim.x) > maxAlongPitch) aim.x = maxAlongPitch * Math.sign(aim.x);
    if (Math.abs(aim.y) > maxAcrossPitch) aim.y = maxAcrossPitch * Math.sign(aim.y);

    // match.cpp:748-751. The shake fades in as the history fills, so the opening seconds of
    // a match are steady.
    const shudder =
      (match.ball.momentum.length() * 0.8 + 6) * 0.2 * (this.history.length / HISTORY_SIZE);
    aim.x += (this.random() * 0.2 - 0.1) * shudder;
    aim.y += (this.random() * 0.2 - 0.1) * shudder;

    this.history.push(aim.clone());
    if (this.history.length > HISTORY_SIZE) this.history.shift();

    this.average.set(this.weightedAverage(this.averaged), timeMs);
  }

  /**
   * The original's weighting curve, `match.cpp:758-767`, with its own two comments:
   * a "healthy mix of latest & middle", then a "sharp cutoff @ latest (because cameraperson
   * can't 'foresee' the current moment that fast)".
   */
  private weightedAverage(out: Vector3): Vector3 {
    out.set(0, 0, 0);
    const size = this.history.length;
    let total = 0;
    for (let index = 0; index < size; index++) {
      const position = index / size;
      let weight = Math.sin((position - 0.3) * 1.4 * Math.PI) * 0.5 + 0.5;
      weight *= Math.pow(1 - position, 0.3);
      out.addScaledVector(this.history[index]!, weight);
      total += weight;
    }
    return total === 0 ? out : out.divideScalar(total);
  }

  /** True once `tick` has run at least once, so `pose` has something to read. */
  get ready(): boolean {
    return this.average.sampleCount > 0;
  }

  /**
   * The camera pose for a render instant, interpolated the same way every other rendered
   * quantity is. `match.cpp:781-791`.
   */
  pose(timeMs: number, out?: CameraPose): CameraPose {
    const average = this.average.get(timeMs);
    const settings = this.settings;

    // Each slider scales one number about the middle of its range, so the middle of every
    // slider is the framing named in FRAMING above. Higher zoom means closer, as it reads.
    const height = FRAMING.height * (0.5 + settings.height);
    const setback = FRAMING.setback * (1.5 - settings.zoom);
    const fovDegrees = FRAMING.fov * (0.7 + settings.fov * 0.6);

    const position = out?.position ?? new Vector3();
    position.set(
      average.x * (1 - settings.angle * 0.2) * (1 - settings.zoom * 0.3),
      // The camera barely follows the play ACROSS the pitch. A camera on its gantry does not
      // slide towards the near touchline when the ball goes there; it pans, and the tilt below
      // does that. Following it properly, as the original did, swings the composition wildly
      // between a shot of the far stand and a shot of the near hoardings.
      average.y * (0.35 - settings.zoom * 0.2) - (PITCH.halfWidth + setback),
      average.z * 0.2 + height,
    );

    // The horizontal-angle slider swings the camera towards a sideline view as the play
    // moves away from the halfway line; at its default of zero it never swings at all.
    const angleFactor = 1 - settings.angle * 0.4;
    const yaw = (-average.x / PITCH.halfLength) * (1 - angleFactor) * 0.25 * Math.PI * 1.24;

    // Tilt so the aim point sits in the middle of the frame. The original derived this from
    // the camera's height alone, which let the aim drift up and down the picture as the play
    // crossed the pitch; with a wide view that drift is the difference between seeing the far
    // stand and seeing the near touchline.
    const groundDistance = Math.hypot(average.y - position.y, average.x - position.x);
    const pitch =
      Math.PI / 2 -
      Math.atan2(position.z - average.z * 0.2, groundDistance) +
      (FRAMING.tiltUp * Math.PI) / 180;

    const pose = out ?? { position, yaw, pitch, fovDegrees: 0, near: 0, far: FAR_CAP };
    pose.position = position;
    pose.yaw = yaw;
    pose.pitch = pitch;
    pose.fovDegrees = fovDegrees;
    // The near plane is as far out as the nearest thing the camera can actually see, which
    // buys back a lot of depth precision. The original simply used the camera's own height and
    // said nothing could be nearer — true only while the view is narrow enough that the bottom
    // of the frame lands well down the pitch. Ours is not, so it is worked out: how far below
    // the horizon the bottom edge of the frame points, and where that ray meets the ground.
    // The margin covers the bottom corners, which dip lower than the bottom edge does.
    const belowHorizon = Math.atan2(position.z, groundDistance) + (fovDegrees * Math.PI) / 360;
    const nearestGround = position.z / Math.sin(Math.min(belowHorizon, Math.PI / 2 - 0.01));
    pose.near = Math.max(1, nearestGround * 0.55);
    pose.far = FAR_CAP;
    return pose;
  }

  /** Write the pose onto a three.js camera, converting into the render frame. */
  apply(camera: PerspectiveCamera, timeMs: number): void {
    if (!this.ready) return;
    const pose = this.pose(timeMs);

    simToRender(pose.position, camera.position);
    this.yawQuat.setFromAxisAngle(Z_AXIS, pose.yaw);
    this.pitchQuat.setFromAxisAngle(X_AXIS, pose.pitch);
    // Node rotation then camera rotation, matching the original's two-node hierarchy
    // (`match.cpp:1222,1238`): the node yaws, the camera pitches inside it.
    this.simQuat.copy(this.yawQuat).multiply(this.pitchQuat);
    simToRenderQuat(this.simQuat, camera.quaternion);

    camera.fov = pose.fovDegrees;
    camera.near = pose.near;
    camera.far = pose.far;
    camera.updateProjectionMatrix();
  }
}
